import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  of,
  switchMap,
} from 'rxjs';
import {
  EmployeeWithdrawalView,
  HrAlertService,
  HrHttpService,
} from '../hr-http.service';

const EMPTY_EMPLOYEE_DETAILS = {
  payrollId: '',
  employeeName: '',
};

@Component({
  selector: 'app-employee-withdrawal',
  templateUrl: './employee-withdrawal.component.html',
  styleUrls: ['./employee-withdrawal.component.scss'],
})
export class EmployeeWithdrawalComponent implements OnDestroy {
  private static readonly MIN_SEARCH_LENGTH = 5;
  private static readonly MIN_REMARKS_LENGTH = 10;
  private static readonly MAX_REMARKS_LENGTH = 250;

  readonly detailFields = [
    { key: 'payrollId', label: 'Payroll Id', id: 'ewd-payroll' },
    { key: 'employeeName', label: 'Employee Name', id: 'ewd-name' },
  ] as const;

  readonly maxRemarksLength = EmployeeWithdrawalComponent.MAX_REMARKS_LENGTH;

  employeeSearch = new FormControl<string | Record<string, unknown>>('');
  remarks = new FormControl('');

  employeeDetailsForm = new FormGroup(
    Object.fromEntries(
      Object.keys(EMPTY_EMPLOYEE_DETAILS).map((key) => [
        key,
        new FormControl({ value: '', disabled: true }),
      ])
    )
  );

  private readonly employeeOptionsSubject = new BehaviorSubject<Record<string, unknown>[]>([]);
  readonly employeeOptions = this.employeeOptionsSubject.asObservable();
  private employeeOptionsList: Record<string, unknown>[] = [];

  selectedEmployee: EmployeeWithdrawalView | null = null;
  isWithdrawing = false;

  private readonly subs = new Subscription();

  constructor(
    private readonly hrHttpService: HrHttpService,
    private readonly hrAlert: HrAlertService,
    private readonly spinner: NgxSpinnerService
  ) {
    this.subs.add(this.buildEmployeeSearchStream().subscribe());
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get hasSelectedEmployee(): boolean {
    return !!this.selectedEmployee;
  }

  get canClear(): boolean {
    if (this.hasSelectedEmployee) {
      return true;
    }
    return this.getSearchText(this.employeeSearch.value).length > 0;
  }

  get remarksLength(): number {
    return String(this.remarks.value ?? '').trim().length;
  }

  get canWithdraw(): boolean {
    const len = this.remarksLength;
    return (
      len >= EmployeeWithdrawalComponent.MIN_REMARKS_LENGTH &&
      len <= EmployeeWithdrawalComponent.MAX_REMARKS_LENGTH
    );
  }

  displayEmployeeLabel = (value: string | Record<string, unknown> | null): string => {
    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    return this.getOptionLabel(value);
  };

  onEmployeeSelected(event: MatAutocompleteSelectedEvent): void {
    const item = event.option.value as Record<string, unknown>;
    if (item && typeof item === 'object') {
      this.applySelectedEmployee(item);
    }
  }

  onWithdraw(): void {
    if (this.isWithdrawing || !this.selectedEmployee || !this.canWithdraw) {
      return;
    }

    const payrollId = String(this.selectedEmployee.payrollId ?? '').trim();
    if (!payrollId) {
      this.hrAlert.show('Please select a valid employee.', 'Warning');
      return;
    }

    const remarks = String(this.remarks.value ?? '').trim();
    const employeeName = this.selectedEmployee.employeeName || payrollId;
    this.hrAlert
      .confirm(`Withdraw employee ${employeeName} (${payrollId})?`, {
        title: 'Confirm Withdrawal',
        confirmLabel: 'Withdraw',
        cancelLabel: 'Cancel',
      })
      .subscribe((confirmed: boolean) => {
        if (!confirmed) {
          return;
        }
        this.submitWithdrawal(payrollId, remarks);
      });
  }

  onClear(): void {
    this.selectedEmployee = null;
    this.employeeDetailsForm.reset({ ...EMPTY_EMPLOYEE_DETAILS });
    this.remarks.setValue('');
    this.employeeSearch.setValue('');
    this.employeeOptionsList = [];
    this.employeeOptionsSubject.next([]);
  }

  trackByPayrollId = (_index: number, item: Record<string, unknown>): string =>
    this.getLookupId(item) || String(_index);

  getOptionPayrollId(item: Record<string, unknown>): string {
    return this.getLookupId(item);
  }

  getOptionName(item: Record<string, unknown>): string {
    return this.pick(item, 'empName', 'employeeName', 'firstName', 'userName', 'name')
      .replace(/\b\d{10}\b/g, '')
      .replace(/\s*\|\s*/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  getOptionLabel(item: Record<string, unknown>): string {
    const id = this.getLookupId(item);
    const name = this.getOptionName(item);
    return id && name ? `${id} - ${name}` : id || name || 'Employee';
  }

  private buildEmployeeSearchStream(): Observable<Record<string, unknown>[]> {
    return this.employeeSearch.valueChanges.pipe(
      map((value) => this.getSearchText(value)),
      distinctUntilChanged(),
      debounceTime(300),
      filter((search) => {
        if (!search) {
          this.employeeOptionsList = [];
          this.employeeOptionsSubject.next([]);
          return false;
        }
        if (search.length < EmployeeWithdrawalComponent.MIN_SEARCH_LENGTH) {
          this.employeeOptionsSubject.next(this.filterLocal(search));
          return false;
        }
        return true;
      }),
      switchMap((search) =>
        this.hrHttpService.searchByEmployees(search).pipe(
          map((res) => this.hrHttpService.normalizeSearchByEmployeesResults(res)),
          catchError(() => {
            this.hrAlert.show('Unable to search employees. Please try again.', 'Error');
            return of(this.filterLocal(search));
          })
        )
      ),
      map((list) => {
        this.employeeOptionsList = list;
        this.employeeOptionsSubject.next(list);
        return list;
      })
    );
  }

  private applySelectedEmployee(item: Record<string, unknown>): void {
    const payrollId = this.getLookupId(item);
    const mapped = this.hrHttpService.mapEmployeeWithdrawalViewData(
      payrollId ? { ...item, payrollId, payroll: payrollId } : item
    );
    this.selectedEmployee = mapped;
    this.employeeDetailsForm.patchValue({
      payrollId: mapped.payrollId,
      employeeName: mapped.employeeName,
    });
    this.employeeSearch.setValue(item, { emitEvent: false });
  }

  private submitWithdrawal(payrollId: string, remarks: string): void {
    this.isWithdrawing = true;
    this.spinner.show();

    this.hrHttpService
      .saveEmployeeWithdrawal({ payrollId, remarks })
      .pipe(
        finalize(() => {
          this.isWithdrawing = false;
          this.spinner.hide();
        })
      )
      .subscribe({
        next: () => {
          this.hrAlert.show('Employee withdrawn successfully.', 'Success');
          this.onClear();
        },
        error: (err) => {
          console.error('Employee withdrawal failed', err);
          this.hrAlert.show('Failed to withdraw employee. Please try again.', 'Error');
        },
      });
  }

  private getSearchText(value: unknown): string {
    if (value == null) {
      return '';
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
      return this.getLookupId(value as Record<string, unknown>);
    }
    const text = String(value).trim();
    return text.includes('|') ? text.split('|')[0]?.trim() || text : text;
  }

  private filterLocal(search: string): Record<string, unknown>[] {
    const q = search.toLowerCase();
    return this.employeeOptionsList.filter((item) =>
      [
        this.getLookupId(item),
        this.pick(item, 'empName', 'employeeName', 'userName', 'name'),
        this.pick(item, 'mobileNo', 'mobileNumber', 'mobile'),
        this.pick(item, 'empId', 'employeeId'),
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }

  private getLookupId(data: Record<string, unknown>): string {
    return this.pick(data, 'payrollId', 'payRollId', 'payroll');
  }

  private pick(item: Record<string, unknown>, ...keys: string[]): string {
    for (const key of keys) {
      const val = item[key];
      if (val != null && String(val).trim() !== '' && String(val) !== '0') {
        return String(val).trim();
      }
    }
    return '';
  }
}
